import { DotsVerticalIcon, PlusCircleIcon } from "@heroicons/react/outline";
import { DropdownMenu } from "components/dropdownMenu";
import { ISimpleEventDetail, ITicketPhasesForm, Phase } from "interfaces";
import { get, isEmpty } from "lodash";
import { useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { ConfirmModal } from "components/confirmModal";
import { useCreate, useTranslation, useUpdate } from "@refinedev/core";
import toast from "react-hot-toast";
import { TicketPhaseModal } from "./ticketPhaseModal";
import { HistoryLodModal } from "components/historyLogModal";
import { FullScreenLoading } from "components/fullScreenLoading";
import { endpointService } from "components/shared/services/endpointService";

const INITIAL_TICKET_PHASE_CONFIG = {
  open: false,
  defaultValue: {},
  submit: (data: any) => {},
};

const INITIAL_ACTION_MODAL_CONFIG = {
  open: false,
  title: "",
  submit: () => {},
};

interface PhaseField extends Phase {
  id: string;
}

interface TicketPhaseProps {
  eventDetailData: ISimpleEventDetail;
  fetchTicketPhases: () => void;
}
export const TicketPhase = ({
  eventDetailData,
  fetchTicketPhases,
}: TicketPhaseProps) => {
  const { translate } = useTranslation();
  const [openLogModalId, setOpenLogModalId] = useState<string | null>(null);

  const [ticketPhaseModalConfig, setTicketPhaseModalConfig] = useState(
    INITIAL_TICKET_PHASE_CONFIG
  );
  const [activePhaseStatusModalConfig, setActivePhaseStatusModalConfig] =
    useState(INITIAL_ACTION_MODAL_CONFIG);

  const { mutate: createMutation, isLoading: isLoading } = useCreate();
  const { mutate: updateMutation } = useUpdate();

  const { control, watch } = useFormContext<ITicketPhasesForm>(); // retrieve all hook methods

  const { fields } = useFieldArray({
    control,
    name: "ticket_phases",
  });

  const _addTicketPhase = (data: any) => {
    createMutation(
      {
        resource: endpointService.getEventEndpoint("createTicketPhase"),
        meta: {
          ignoreResourceWrapper: true,
        },
        values: data,
      },
      {
        onSuccess: (data) => {
          fetchTicketPhases();
          toast.success(translate("toastMessage.addedSuccessfully"));
        },
      }
    );
  };

  const _updateTicketPhase = (data: Phase, index: number) => {
    updateMutation(
      {
        resource: endpointService.getEventEndpoint("updateTicketPhase", {
          id: data.id ?? "",
        }),
        id: "",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: data,
      },
      {
        onSuccess: (data) => {
          fetchTicketPhases();
          toast.success(translate("toastMessage.updatedSuccessfully"));
        },
      }
    );
  };

  const _onCloseModal = () => {
    setTicketPhaseModalConfig(INITIAL_TICKET_PHASE_CONFIG);
  };

  const _onCloseActivePhaseModal = () => {
    setActivePhaseStatusModalConfig({
      ...INITIAL_ACTION_MODAL_CONFIG,
      title: activePhaseStatusModalConfig.title,
    });
  };

  const phasesData = watch("ticket_phases");

  return (
    <div className="box-container p-4">
      <div>
        <div className="grid gap-y-4 sm:grid-cols-6">
          {fields.map((phase: PhaseField, index: number) => (
            <div
              className="col-span-full flex flex-col gap-y-2 sm:flex-row items-start justify-between rounded-md border border-gray-300 px-4 py-2.5"
              key={phase.id}
            >
              <div className="flex flex-row flex-wrap">
                {eventDetailData.languages.map((lang, langIndex) => (
                  <p key={lang}>
                    {get(phasesData[index], `name.${lang}`)}{" "}
                    {langIndex !== eventDetailData.languages.length - 1 ? (
                      <span className="mx-1.5">/</span>
                    ) : (
                      <></>
                    )}
                  </p>
                ))}
              </div>
              <div className="flex flex-row items-center self-end sm:self-start gap-x-2">
                <span
                  className={`w-fit inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ring-1 ring-inset ${
                    phasesData[index].is_active
                      ? "bg-green-100 ring-green-100 text-green-700"
                      : "bg-magnolia-500 text-neutral-800 ring-magnolia-600"
                  }`}
                >
                  {phasesData[index].is_active
                    ? translate("active")
                    : translate("in-active")}
                </span>
                {phasesData[index].is_active ? (
                  <span
                    className={`w-fit inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ring-1 ring-inset ${
                      !phasesData[index].hide_in_ui
                        ? "bg-green-100 ring-green-100 text-green-700"
                        : "bg-magnolia-500 text-neutral-800 ring-magnolia-600"
                    }`}
                  >
                    {phasesData[index].hide_in_ui
                      ? translate("hide")
                      : translate("show")}
                  </span>
                ) : (
                  <></>
                )}
                <DropdownMenu
                  label={
                    <DotsVerticalIcon className="w-4 h-4 text-brand-500" />
                  }
                  items={[
                    ...(phasesData[index].is_active &&
                    !phasesData[index].read_only
                      ? [
                          {
                            label: translate("edit"),
                            onClick: () =>
                              setTicketPhaseModalConfig({
                                open: true,
                                defaultValue:
                                  watch("ticket_phases")[index].name,
                                submit: (data) => {
                                  _updateTicketPhase(
                                    {
                                      ...watch("ticket_phases")[index],
                                      name: data,
                                    },
                                    index
                                  );
                                },
                              }),
                          },
                        ]
                      : []),
                    {
                      label: phasesData[index].is_active
                        ? translate("deactivate")
                        : translate("activate"),
                      onClick: () =>
                        setActivePhaseStatusModalConfig({
                          open: true,
                          title: translate("modal.toggleStatus", {
                            status: phasesData[index].is_active
                              ? translate("deactivate")
                              : translate("activate"),
                            object: translate(
                              "eventSetup.phaseAndTicket.phaseName"
                            ),
                          }),
                          submit: () => {
                            _updateTicketPhase(
                              {
                                ...watch("ticket_phases")[index],
                                is_active: phasesData[index].is_active
                                  ? false
                                  : true,
                              },
                              index
                            );
                          },
                        }),
                    },
                    ...(phasesData[index].is_active
                      ? [
                          {
                            label: phasesData[index].hide_in_ui
                              ? translate("show")
                              : translate("hide"),
                            onClick: () =>
                              setActivePhaseStatusModalConfig({
                                open: true,
                                title: translate("modal.toggleStatus", {
                                  status: phasesData[index].hide_in_ui
                                    ? translate("show")
                                    : translate("hide"),
                                  object: translate(
                                    "eventSetup.phaseAndTicket.phaseName"
                                  ),
                                  addition: translate("inProd"),
                                }),
                                submit: () => {
                                  _updateTicketPhase(
                                    {
                                      ...watch("ticket_phases")[index],
                                      hide_in_ui: phasesData[index].hide_in_ui
                                        ? false
                                        : true,
                                    },
                                    index
                                  );
                                },
                              }),
                          },
                        ]
                      : []),
                    {
                      label: "Log",
                      onClick: () =>
                        setOpenLogModalId(phasesData[index].id ?? ""),
                    },
                  ]}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="text-center text-sm">
          {isEmpty(fields) ? (
            <p>{translate("eventSetup.phaseAndTicket.noPhase")}</p>
          ) : (
            <></>
          )}
        </div>
      </div>
      <div className="add_edit_phase">
        <div className="w-full text-center">
          <button
            type="button"
            className="flex flex-row items-center mx-auto mt-4 w-fit py-2.5 px-4 text-sm bg-white text-brand-500 active:bg-gray-50"
            // onClick={handleAddPhase}
            onClick={() =>
              setTicketPhaseModalConfig({
                open: true,
                defaultValue: {},
                submit: (data) =>
                  _addTicketPhase({
                    name: data,
                    hide_in_ui: false,
                    is_active: true,
                    event_id: eventDetailData.id,
                  }),
              })
            }
          >
            <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
            <span className="shrink-0 text-sm">{translate("eventSetup.phaseAndTicket.addMorePhase")}</span>
          </button>
        </div>

        <TicketPhaseModal
          languages={eventDetailData.languages}
          open={ticketPhaseModalConfig.open}
          onClose={_onCloseModal}
          defaultValue={ticketPhaseModalConfig.defaultValue}
          onSubmit={ticketPhaseModalConfig.submit}
        />
        <ConfirmModal
          key="active-phase"
          title={activePhaseStatusModalConfig.title}
          content=""
          open={activePhaseStatusModalConfig.open}
          onConfirm={activePhaseStatusModalConfig.submit}
          onClose={_onCloseActivePhaseModal}
        />
        <HistoryLodModal
          resource={endpointService.getEventEndpoint(
            "getTicketPhaseHistoryLog",
            {
              logId: openLogModalId ?? "",
            }
          )}
          open={openLogModalId ? true : false}
          onClose={() => setOpenLogModalId(null)}
          key="ticket-phase"
          id="ticket-phase"
        />
        {isLoading ? <FullScreenLoading /> : <></>}
      </div>
    </div>
  );
};
